import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { format } from "date-fns";
import { useState } from "react";
import "react-day-picker/style.css";
import RGL, { WidthProvider } from "react-grid-layout";

import { SiteDashboard } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { Icon } from "~/assets/icons/Icon";
import Breadcrumbs from "~/components/ui/breadcrumbs/Breadcrumbs";
import Button from "~/components/ui/button/Button";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { routes } from "~/router";

import { DASHBOARD_NUM_COLUMNS, QUERY_KEYS } from "../../constants";
import { joinWidgets } from "../../utils/joinWidgets";
import { useIsScrolling } from "../../utils/useIsScrolling";
import { DashboardViewWidget } from "../DashboardViewWidget/DashboardViewWidget";
import { DateRangePicker } from "../DateRangePicker/DateRangePicker";
import { useDeleteDashboard } from "../DeleteDashboardModal";
import { DynamicSettingsIcon } from "../DynamicSettingIcon";
import { useRenameDashboard } from "../RenameDashboardModal/useRenameDashboard";
import styles from "./DashboardView.module.scss";
import { DashboardViewSkeleton } from "./DashboardViewSkeleton";
import { ResizeHandle } from "./ResizeHandle";
import { useDateRangeFilter } from "./utils/useDateRangeFilter";
import { useLayout } from "./utils/useLayout";
import { useUpdateDashboard } from "./utils/useUpdateDashboard";

const RGLComponent = WidthProvider(RGL);

export const DashboardView = ({
  siteId,
  dashboard,
}: {
  siteId: number;
  dashboard: SiteDashboard;
}) => {
  const [manipulatedWidgetId, setManipulatedWidgetId] = useState<string | null>(
    null,
  );
  const [isEditing, setIsEditing] = useState(false);
  const { range, setRange, isPending } = useDateRangeFilter();
  const { onDelete, DeleteModal } = useDeleteDashboard({
    siteId,
    dashboardId: dashboard.id,
  });

  const { data, isLoading: isWidgetsLoading } = useQuery({
    queryKey: QUERY_KEYS.DASHBOARD_DATA(
      siteId,
      dashboard.id,
      format(range.from, "yyyy-MM-dd"),
      format(range.to, "yyyy-MM-dd"),
    ),
    queryFn: async () => {
      const response = await apiClient.sites.dashboardsViewDetail(
        siteId,
        dashboard.id,
        {
          start_date: format(range.from, "yyyy-MM-dd"),
          end_date: format(range.to, "yyyy-MM-dd"),
        },
      );

      return response.data.site_dashboard_view.data.widgets;
    },
  });

  const { updateWidgets, updateSingleWidget, updateName } = useUpdateDashboard(
    siteId,
    dashboard,
  );

  const { RenameModal, onRename } = useRenameDashboard({
    dashboard,
    updateName,
  });

  const isScrolling = useIsScrolling();
  const [editingWidgetId, setEditingWidgetId] = useState<string | null>(null);

  const widgets = isWidgetsLoading
    ? []
    : joinWidgets(dashboard.properties?.widgets ?? [], data!);
  const { layout, onLayoutChange } = useLayout({
    widgets,
    updateWidgets,
  });

  if (isWidgetsLoading) return <DashboardViewSkeleton />;

  const breadcrumbs = [
    { title: PAGE_TITLES.dashboard, link: routes.home() },
    {
      title: PAGE_TITLES.googleDashboard,
      link: routes.site.googleDashboard({ id: String(siteId) }),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.wrapper}>
        <div>
          <h1
            className={clsx(styles.title, {
              [styles.titleClickable]: isEditing,
            })}
            {...(isEditing && {
              onClick: onRename,
              role: "button",
              ariaLabel: "Rename dashboard",
            })}
          >
            {dashboard.name}
          </h1>
          <Breadcrumbs fakeBreadcrumbs={breadcrumbs} />
        </div>
        <div className={styles.headerActions}>
          {isEditing && (
            <Button unstyled onClick={onDelete}>
              <Icon
                iconName="TrashIcon"
                color="var(--service-red3)"
                style={{ verticalAlign: "-2px" }}
              />
            </Button>
          )}
          <Button unstyled onClick={() => setIsEditing(!isEditing)}>
            <DynamicSettingsIcon isEditing={isEditing} />
          </Button>
          <DateRangePicker
            value={range}
            onChange={setRange}
            disabled={isPending || isEditing}
          />
        </div>
      </div>
      <div className={styles.rglWrapper}>
        <RGLComponent
          layout={layout}
          onLayoutChange={onLayoutChange}
          cols={DASHBOARD_NUM_COLUMNS}
          rowHeight={300}
          margin={[16, 16]}
          draggableHandle=".rgl-drag-handle"
          resizeHandles={["s", "e", "w"]}
          isResizable={isEditing}
          isDraggable={isEditing}
          // @ts-expect-error TS2322 - `ref` argument is undocumented
          resizeHandle={(axis, ref) => (
            <ResizeHandle isVisible={isEditing} axis={axis} ref={ref} />
          )}
          onResizeStart={(_layout, placement) => {
            setManipulatedWidgetId(placement.i);
          }}
          onResizeStop={() => {
            setManipulatedWidgetId(null);
          }}
          onDragStart={(_layout, placement) => {
            setManipulatedWidgetId(placement.i);
          }}
          onDragStop={() => {
            setManipulatedWidgetId(null);
          }}
        >
          {widgets.map((widget) => (
            <div
              key={widget.id}
              className={clsx(styles.widgetWrapper, {
                [styles.zIndexOne]: manipulatedWidgetId === widget.id,
              })}
            >
              <DashboardViewWidget
                widget={widget}
                lockScrolling={isScrolling}
                showDragHandle={isEditing}
                isEditing={editingWidgetId === widget.id}
                onEditToggle={(widgetId) => {
                  if (editingWidgetId !== widgetId) {
                    setEditingWidgetId(widgetId);

                    return;
                  }

                  setEditingWidgetId(null);
                }}
                updateSingleWidget={updateSingleWidget}
              />
            </div>
          ))}
        </RGLComponent>
        <div
          className={clsx(styles.rglWrapperLoadingOverlay, {
            [styles.rglWrapperLoadingOverlayVisible]: isPending,
          })}
        />
      </div>
      <DeleteModal />
      <RenameModal />
    </div>
  );
};
